import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { datadogRumService } from '@lib/datadogRum';

/**
 * Routes where a session replay is always recorded. Nothing else records one:
 * sessionReplaySampleRate is 0 and the SDK is in manual mode, so a session
 * that never reaches one of these paths is never recorded.
 *
 * FFH-1932 forced recording at funnel ENTRY (/ai-team-rating, /quiz, /join,
 * /funnel-welcome) so a replay would include the journey into checkout. That
 * turned out to cover the busiest routes on the site: /ai-team-rating alone
 * was touched by 11,498 of the 17,286 web sessions Datadog retained over the
 * two days to 26 Aug 2026, and because forcing is sticky for the rest of the
 * session it pulled in homepage sessions that clicked through as well. 81% of
 * retained sessions carried a replay.
 *
 * FFH-2736 cut the list back to the payment step. The cost is that recording
 * only rolls forward, so a replay now starts at checkout: a payment failure
 * still has one, but not the pages that led to it. If that journey is needed
 * again, force on a PERCENTAGE of funnel entries rather than all of them.
 */
const FORCED_REPLAY_ROUTE_PREFIXES = ['/checkout-stripe', '/member-upgrade'];

/**
 * Whole-segment match, same shape as lib/checkout/initiating-page.ts:
 * '/member-upgrade' must match '/member-upgrade' and '/member-upgrade/x' but
 * not a future '/member-upgrade-offer', which merely shares the string prefix.
 */
const isForcedReplayPath = (path: string): boolean =>
  FORCED_REPLAY_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );

/**
 * Force session replay recording when the visitor is on a payment route, on
 * mount and after every client transition. Forcing is idempotent, so repeat
 * calls on later navigations are harmless. Mount at the top of `HubApp`
 * alongside the other route-subscribed hooks.
 */
export const useForceReplayOnPaymentRoutes = (): void => {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const maybeForceReplay = (url: string): void => {
      const path = url.split(/[?#]/)[0];
      if (isForcedReplayPath(path)) {
        datadogRumService.forceSessionReplay();
      }
    };

    maybeForceReplay(window.location.pathname);
    router.events.on('routeChangeComplete', maybeForceReplay);
    return () => {
      router.events.off('routeChangeComplete', maybeForceReplay);
    };
  }, [router.events]);
};
